import { createFilterOptions, FilterOptionsState } from '@material-ui/lab';
import { getIn, useFormikContext } from 'formik';
import React, { useCallback, useContext, useMemo } from 'react';
import { v4 } from 'uuid';

import { PRODUCTS_PAGE } from 'src/constants';
import { FlagsContext } from 'src/context';
import useNavigate from 'src/hooks/useNavigate';
import { useAppSelector } from 'src/hooks/useStore';
import { GetDefaultCurrency } from 'src/legacy/components/Formatters';
import { ProductSchema } from 'src/legacy/components/Products/ProductEditPage/ProductEditPage';
import {
  LineItemFormSchema,
  PresentationProps,
} from 'src/routes/billing/components/LineItemEditorV2/LineItem/Presentation';
import { LineItemEditorPricingMode } from 'src/routes/billing/components/LineItemEditorV2/LineItemEditor';
import { LineItemType } from 'src/services/api/invoicesApi';
import {
  CreatePriceInput,
  PriceType,
  Product,
  useAddPriceByProductIdMutation,
  useAddProductMutation,
  useGetPricesByPortalQuery,
  useGetProductsQuery,
} from 'src/services/api/productsApi';
import { SubscriptionInterval } from 'src/types/subscriptions';
import { arrayToMap } from 'src/utils/array';

import { FilterOption } from '../types';

export type LineItemFormSchemaExtended = LineItemFormSchema & {
  name?: string;
};

export type LineItemField = keyof LineItemFormSchemaExtended;

const isFilterOption = <AllowRecurringPrices extends boolean>(
  value: string | null | FilterOption<AllowRecurringPrices>,
): value is FilterOption<AllowRecurringPrices> =>
  value !== null && typeof value !== 'string';

type Props<Schema, AllowRecurringPrices extends boolean> = {
  itemIndex: number;
  onRemoveLineItem: (index: number) => void;
  // If true, the editor will ensure all line items have the same billing period
  allowRecurringPrices: AllowRecurringPrices;
  getLineItems: (o: Schema) => LineItemFormSchemaExtended[];
  // This should be something like "fields.lineItems" or "lineItems"
  formikLineItemFieldName: string;
  formikAmountFieldName: string;
  lineItemsInCents?: boolean;
  draftLineItem: LineItemFormSchemaExtended | null;
  setdraftLineItem: (draftlineItems: LineItemFormSchemaExtended | null) => void;
  interval?: SubscriptionInterval;
  addProductRenderer: React.ReactNode;
  mode: LineItemEditorPricingMode;
  onLineItemSelected?: (
    lineItem: LineItemFormSchemaExtended,
    itemIndex: number,
  ) => void;
  // While `onRemoveLineItem` is used to remove the line item from the list,
  // this is used to reset the line item in the formik state so that element stays but in default state
  onResetLineItem?: (itemIndex: number) => void;
  allowCreatingNewProduct: boolean;
  allowCreatingNewPrice: boolean;
  allowAddNewOneTimeItem: boolean;
  renderPresentationComponent: (
    props: PresentationProps<AllowRecurringPrices>,
  ) => JSX.Element;
  isQuantityRequired?: boolean;
  showAllIntervalPrices?: boolean;
  restrictProductPriceReuse?: boolean;
  fieldName: LineItemField;
  showCreateNewProductItem: boolean;
  isDragging?: boolean;
};

const ADD_NEW_ONE_OFF_ITEM_ID = 'add-new-option-item';
export const CREATE_NEW_PRODUCT_ITEM_ID = 'create-new-product';
const NO_OPTION = 'no_option';
const ADD_NEW_PRODUCT_ITEM = {
  id: 'add-new-product',
  name: '',
  type: LineItemType.OneTime,
};
const MANAGE_PRODUCT_ITEM = {
  id: 'manage-products',
  name: 'Manage products',
  type: LineItemType.OneTime,
};
export enum LineItemOptions {
  AddNewOneOffItem = 'add-new-option-item',
  CreateNewProductItem = 'create-new-product',
  NoOption = 'no_option',
  AddNewProductItem = 'add-new-product',
  ManageProductItem = 'manage-products',
}

const filter = createFilterOptions<FilterOption<boolean>>();

export function Controller<Schema, AllowRecurringPrices extends boolean>({
  itemIndex,
  onRemoveLineItem,
  allowRecurringPrices,
  getLineItems,
  formikLineItemFieldName,
  formikAmountFieldName,
  lineItemsInCents = true,
  draftLineItem,
  setdraftLineItem,
  addProductRenderer,
  interval,
  mode,
  onLineItemSelected,
  onResetLineItem,
  allowCreatingNewProduct,
  allowCreatingNewPrice,
  allowAddNewOneTimeItem = true,
  renderPresentationComponent,
  isQuantityRequired = true,
  showAllIntervalPrices,
  restrictProductPriceReuse,
  fieldName,
  showCreateNewProductItem,
  isDragging,
}: Props<Schema, AllowRecurringPrices>) {
  const {
    setFieldValue,
    values,
    handleChange,
    setFieldTouched,
    initialValues,
    errors,
  } = useFormikContext<Schema>();

  const accountInfo = useAppSelector((state) => state.payments.accountInfo);
  const defaultCurrency = accountInfo?.default_currency || GetDefaultCurrency();

  const { data: _products = [] } = useGetProductsQuery();
  const { data: portalPrices = [] } = useGetPricesByPortalQuery();
  const portalPricesMap = useMemo(
    () => arrayToMap(portalPrices, 'ref'),
    [portalPrices],
  );
  const [addPrice, { isLoading: isAddPriceSavingInProgress }] =
    useAddPriceByProductIdMutation();
  const [addProduct, { isLoading: isAddProductSavingInProgress }] =
    useAddProductMutation();
  const { navigate } = useNavigate();
  const { EnableImmersiveProductCreationFlow } = useContext(FlagsContext);
  // state to store the draft line item when the line item is being edited
  const lineItems = getLineItems(values);
  const descriptionFieldName = `${formikLineItemFieldName}[${itemIndex}].${fieldName}`;

  const amountFieldName = `${formikLineItemFieldName}[${itemIndex}].amount`;
  // This is temporary until we consolidate the names to be rate across the board.
  const rateFieldName = `${formikLineItemFieldName}[${itemIndex}].rate`;
  const priceIdFieldName = `${formikLineItemFieldName}[${itemIndex}].priceId`;
  const typeFieldName = `${formikLineItemFieldName}[${itemIndex}].type`;
  const isEditingFieldName = `${formikLineItemFieldName}[${itemIndex}].isEditing`;
  const isValidFieldName = `${formikLineItemFieldName}[${itemIndex}].isValid`;
  const quantityFieldName = `${formikLineItemFieldName}[${itemIndex}].quantity`;
  const shouldAddPriceFieldName = `${formikLineItemFieldName}[${itemIndex}].shouldAddPrice`;
  const shouldAddProductFieldName = `${formikLineItemFieldName}[${itemIndex}].shouldAddProduct`;
  const lineItem = useMemo(() => lineItems[itemIndex], [lineItems, itemIndex]);
  const errorQuantity = getIn(errors, quantityFieldName);
  const amountErrors = getIn(errors, amountFieldName);
  const errorDescription = getIn(errors, descriptionFieldName);
  const showAllProducts = mode === LineItemEditorPricingMode.All;
  const isOneTimeStore = mode === LineItemEditorPricingMode.OneTimeStore;
  const isSubscriptionStore =
    mode === LineItemEditorPricingMode.subscriptionsStore;
  const isOneTime = mode === LineItemEditorPricingMode.OneTime;
  const isBelongsToStore = isOneTimeStore || isSubscriptionStore;

  const selectedProductItem = {
    id: lineItems[itemIndex].id,
    name: lineItems[itemIndex].description,
    type: lineItems[itemIndex].type,
    ...(EnableImmersiveProductCreationFlow &&
      !isOneTime &&
      'productId' in lineItems[itemIndex] && {
        productId: lineItems[itemIndex].productId,
      }),
  };

  const products: Product[] = useMemo(() => {
    let __products = _products.map((p) => {
      return {
        ...p,
        priceIds: p.priceIds.filter((pId) => {
          const pidType = portalPricesMap.get(pId);

          // If we allow all prices, we don't need to filter by billing period
          if (showAllProducts) return true;

          // If showAllIntervalPrices is true, include all recurring prices regardless of interval
          if (showAllIntervalPrices && pidType?.interval) return true;

          return allowRecurringPrices
            ? !!pidType?.interval
            : !pidType?.interval;
        }),
      };
    });

    if (showAllProducts || showAllIntervalPrices) {
      // When showAllIntervalPrices is true, we still filter out products with no valid prices
      return __products.filter((p) => p.priceIds.length > 0);
    }

    if (allowRecurringPrices) {
      const productLineItems = lineItems.filter(
        (x) => x.type === LineItemType.Product,
      );
      const firstProductLineItem = productLineItems.at(0);

      const selectedPrice = portalPricesMap.get(
        firstProductLineItem?.priceId || '',
      );
      const targetBillingPeriod = selectedPrice?.interval;
      const shouldFilterPricesByBillingPeriod = productLineItems.length > 1;

      __products = __products.map((p) => {
        const priceIds = p.priceIds.filter((pId) => {
          const billingPeriod = portalPricesMap.get(pId)?.interval;
          return (
            !!billingPeriod &&
            (!shouldFilterPricesByBillingPeriod ||
              billingPeriod === targetBillingPeriod)
          );
        });
        return {
          ...p,
          priceIds,
        };
      });
    }
    return __products.filter((p) => !!p && p.priceIds.length > 0);
  }, [
    _products,
    portalPricesMap,
    allowRecurringPrices,
    showAllProducts,
    showAllIntervalPrices,
    lineItems,
  ]);

  const firstProductLineItem = useMemo(() => {
    return lineItems.find((x) => x.type === LineItemType.Product);
  }, [lineItems]);

  // I get there's a DRYER way to define firstProductLineItem and
  // firstProductLineItemIndex, but it's a slight performance improvement
  // in exchange for typescript not understanding the type narrowing.
  // Honestly, not worth it.
  const firstProductLineItemIndex = useMemo(() => {
    return lineItems.findIndex((x) => x.type === LineItemType.Product);
  }, [lineItems]);

  const hasFirstSelectedPrice = !!firstProductLineItem?.priceId;

  const firstSelectedPrice = useMemo(() => {
    return portalPricesMap.get(firstProductLineItem?.priceId || '');
  }, [firstProductLineItem, portalPricesMap]);

  // This populates the dropdown with the product prices
  // when creating invoice from a template, we need
  // to get the line item prices from the selected product id.
  // The payload is available in the fetched line item prices
  // and not in the line item form values.
  const getValidPricesForLineItem = useCallback(
    (selectedProduct: Product | undefined, inserting: boolean = false) => {
      if (!selectedProduct) return [];
      let selectedProductPrices = selectedProduct.priceIds
        .map((pId) => portalPricesMap.get(pId))
        .filter(Boolean);

      // Filter out prices already used by other line items for the same product if restrictProductPriceReuse is true
      if (restrictProductPriceReuse) {
        const usedPriceIds = lineItems
          .filter(
            (li, itemCurrentIndex) =>
              itemCurrentIndex !== itemIndex &&
              li.type === LineItemType.Product &&
              li.productId === selectedProduct.id &&
              li.priceId,
          )
          .map((li) => ('priceId' in li ? li.priceId : undefined));

        selectedProductPrices = selectedProductPrices.filter(
          (p) => !usedPriceIds.includes(p.ref),
        );
      }

      // If showAllProducts is true, return all remaining prices without filtering
      if (showAllProducts) {
        return selectedProductPrices.sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );
      }

      if (allowRecurringPrices) {
        const productLineItems = lineItems.filter(
          (x) => x.type === LineItemType.Product,
        );

        // We start enforcing equal billing periods once we have
        // selected our first price. The inserting flag is used
        // during the insertion callback, when the 2nd line item
        // has not yet been added to the formik values.
        const shouldRespectBillingPeriod =
          !showAllIntervalPrices &&
          firstProductLineItem &&
          hasFirstSelectedPrice &&
          (productLineItems.length > 1 || inserting);

        selectedProductPrices = selectedProductPrices
          .filter((p) => !!p.interval)
          .filter((p) =>
            shouldRespectBillingPeriod
              ? p.interval === firstSelectedPrice?.interval
              : true,
          );
      } else {
        selectedProductPrices = selectedProductPrices.filter(
          (p) => !p.interval,
        );
      }

      return selectedProductPrices
        .filter(
          (price) =>
            price.currency.toLowerCase() === defaultCurrency.toLowerCase(),
        )
        .sort(
          (a, b) =>
            new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime(),
        );
    },
    [
      defaultCurrency,
      portalPricesMap,
      lineItems,
      allowRecurringPrices,
      firstProductLineItem,
      hasFirstSelectedPrice,
      showAllIntervalPrices,
      firstSelectedPrice,
      showAllProducts,
      itemIndex,
      restrictProductPriceReuse,
    ],
  );

  const pricesDropdownOptions = useMemo(() => {
    if (lineItem.type === LineItemType.OneTime) return [];
    return getValidPricesForLineItem(
      products.find((p) => p.id === lineItem.productId),
    );
  }, [lineItem, getValidPricesForLineItem, products, lineItems]);

  const productsOptions = useMemo(() => {
    if (!products) return [];

    let productsToUse;
    const productLineItems = lineItems.filter(
      (x) => x.type === LineItemType.Product,
    );
    const shouldFilterPricesByBillingPeriod =
      // Only filter if we're allowing recurring prices
      allowRecurringPrices &&
      !showAllIntervalPrices &&
      // If we have more than one product line item, we
      // should always filter
      (productLineItems.length > 1 ||
        // If we only have one product line item, do not
        // filter the options of the first line item.
        // This is so we can allow that first line item
        // to freely change, since there are no other
        // product line items to enforce the same billing
        // period.
        // We check for 'productId' in lineItem because a
        // an unselected line item will not have a productId
        (productLineItems.length === 1 && !('productId' in lineItem)));

    if (shouldFilterPricesByBillingPeriod) {
      productsToUse = products
        .map((p) => {
          const period = firstSelectedPrice?.interval;
          return {
            ...p,
            priceIds: p.priceIds.filter((pId) => {
              const price = portalPricesMap.get(pId);
              return price?.interval === period;
            }),
          };
        })
        .filter((p) => p.priceIds.length > 0);
    } else {
      productsToUse = products;
    }

    // Filter out products whose valid prices are all used if restrictProductPriceReuse is true
    if (restrictProductPriceReuse) {
      const usedPriceIdsByProduct = lineItems.reduce<
        Record<string, Set<string>>
      >((acc, li, itemCurrentIndex) => {
        if (
          itemCurrentIndex !== itemIndex &&
          li.type === LineItemType.Product &&
          li.productId &&
          li.priceId
        ) {
          if (!acc[li.productId]) {
            acc[li.productId] = new Set();
          }
          acc[li.productId].add(li.priceId);
        }
        return acc;
      }, {});

      productsToUse = productsToUse.filter((p) => {
        const validPrices = getValidPricesForLineItem(p);
        const usedPrices = usedPriceIdsByProduct[p.id] || new Set();
        return validPrices.some((price) => !usedPrices.has(price.ref));
      });
    }

    const priceOptions: FilterOption<boolean>[] = [];

    productsToUse.forEach((product) => {
      const validPrices = getValidPricesForLineItem(product);
      validPrices.forEach((price) => {
        priceOptions.push({
          id: price.ref,
          name: `${product.name}`,
          amount: Number(price.amount).toString(),
          type: LineItemType.Product,
          productId: product.id,
          interval: price.interval,
          createdAt: price.createdAt,
        });
      });
    });

    const sortedPriceOptions = priceOptions.sort((a, b) =>
      (b.createdAt || '').localeCompare(a.createdAt || ''),
    );

    return [
      ...(EnableImmersiveProductCreationFlow && showCreateNewProductItem
        ? [
            {
              id: CREATE_NEW_PRODUCT_ITEM_ID,
              name: '',
              type: LineItemType.OneTime,
            },
          ]
        : []),
      ...(!lineItem[fieldName] && allowAddNewOneTimeItem
        ? [ADD_NEW_PRODUCT_ITEM]
        : []),
      ...(EnableImmersiveProductCreationFlow && !isOneTime
        ? sortedPriceOptions
        : productsToUse.map((p) => ({
            id: p.id,
            name: p.name,
            type: LineItemType.Product,
          }))),
    ];
  }, [
    products,
    lineItems,
    allowRecurringPrices,
    showAllIntervalPrices,
    lineItem,
    restrictProductPriceReuse,
    EnableImmersiveProductCreationFlow,
    showCreateNewProductItem,
    fieldName,
    allowAddNewOneTimeItem,
    isOneTime,
    firstSelectedPrice?.interval,
    portalPricesMap,
    itemIndex,
    getValidPricesForLineItem,
  ]);

  // when the autocomplete dropdown item is selected,
  // it saves the selected item to the formik field

  const handleFilterOptions = useCallback(
    (
      options: FilterOption<AllowRecurringPrices>[],
      filterState: FilterOptionsState<FilterOption<AllowRecurringPrices>>,
    ) => {
      if (options.length === 0 && isBelongsToStore) {
        return [
          {
            id: NO_OPTION,
            name: filterState.inputValue,
            type: LineItemType.OneTime,
          },
        ];
      }
      if (!filterState.inputValue) {
        filterState.inputValue =
          typeof lineItem[fieldName] === 'string' ? lineItem[fieldName] : '';
      }
      const trimmedInputValue = filterState.inputValue.trim();
      let filtered = filter(options, filterState);

      if (trimmedInputValue) {
        // in order to keep showing the "Manage products" option at the top of the list,
        // we need to remove it from the filtered list and then add it back at the top.
        filtered = filtered.filter(
          (option) => option.id !== MANAGE_PRODUCT_ITEM.id,
        );
        const isSameProductExist = products.some(
          (p) => p.name.toLowerCase() === trimmedInputValue.toLowerCase(),
        );
        filtered = [
          ...(EnableImmersiveProductCreationFlow && showCreateNewProductItem
            ? [
                {
                  id: CREATE_NEW_PRODUCT_ITEM_ID,
                  name: '',
                  type: LineItemType.OneTime,
                },
              ]
            : []),
          ...((isBelongsToStore || showAllProducts) && filtered.length === 0
            ? [
                {
                  id: NO_OPTION,
                  name: filterState.inputValue,
                  type: LineItemType.OneTime,
                },
              ]
            : []),
          ...(isSameProductExist || !allowAddNewOneTimeItem
            ? []
            : [
                {
                  id: ADD_NEW_ONE_OFF_ITEM_ID,
                  name: filterState.inputValue,
                  type: LineItemType.OneTime,
                },
              ]),
          ...filtered,
        ];
      }
      return filtered;
    },
    [
      initialValues,
      itemIndex,
      lineItems,
      productsOptions,
      getLineItems,
      allowRecurringPrices,
      showAllProducts,
    ],
  );
  const handlePriceChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
      let amount;
      if (lineItem.type === LineItemType.Product) {
        const price = pricesDropdownOptions.find(
          (p) => p.ref === event.target.value,
        );
        amount = price && (price.amount ? price.amount : (price as any).rate);
        amount = (Number(amount) / 100).toString();
      } else {
        amount = event.target.value;
      }

      if (amount) {
        setFieldValue(amountFieldName, amount);
        setFieldValue(rateFieldName, amount);
      }

      handleChange(event);
    },
    [
      lineItem,
      amountFieldName,
      rateFieldName,
      pricesDropdownOptions,
      handleChange,
      setFieldValue,
    ],
  );

  const handleLineItemSelection = useCallback(
    async (
      event: React.ChangeEvent<any>,
      value: string | FilterOption<AllowRecurringPrices> | null,
    ) => {
      const inputValue = event.target.value;
      if (inputValue) {
        setFieldValue(descriptionFieldName, inputValue);
      }
      // Clear input if no value is selected
      if (!value && !inputValue) {
        setFieldValue(descriptionFieldName, '');
        setFieldValue(amountFieldName, '');
        setFieldValue(priceIdFieldName, '');
        setFieldValue(`fields.lineItems[${itemIndex}].productId`, '');
        setFieldValue(
          typeFieldName,
          isBelongsToStore ? LineItemType.Product : LineItemType.OneTime,
        );
        setFieldValue(isEditingFieldName, false);
        // When an item is cleared, we need to reset the field touched status so that the error message for `totalAmount` is not shown
        setFieldTouched(amountFieldName, false);
        setFieldTouched(rateFieldName, false);
      }

      if (showAllProducts) {
        setFieldValue(typeFieldName, LineItemType.Product);
        setFieldValue(quantityFieldName, '1');
      }

      // If the user presses enter and no option is selected from menu list, we should add the item as a one-time item
      if (
        !isFilterOption(value) &&
        inputValue &&
        'key' in event &&
        event.key === 'Enter'
      ) {
        if (isBelongsToStore) {
          setFieldValue(descriptionFieldName, '');
        } else {
          setFieldValue(descriptionFieldName, inputValue);
          setFieldValue(typeFieldName, LineItemType.OneTime);
          setFieldValue(amountFieldName, '');
          setFieldValue(rateFieldName, '');
          setFieldValue(isEditingFieldName, true);
          return;
        }
      }

      // One time line item selection using input value. No need to fetch product price
      if (!isFilterOption(value)) return;

      // when "add {inputValue} as one off item" option is clicked, capture the
      // input value and save it to the formik field.
      if (
        value.id === ADD_NEW_ONE_OFF_ITEM_ID ||
        value.id === ADD_NEW_PRODUCT_ITEM.id
      ) {
        setFieldValue(descriptionFieldName, value.name);
        setFieldValue(typeFieldName, LineItemType.OneTime);
        setFieldValue(amountFieldName, '');
        setFieldValue(rateFieldName, '');
        setFieldValue(isEditingFieldName, true);
        setFieldTouched(descriptionFieldName, false);
        setFieldTouched(amountFieldName, false);
        setFieldTouched(rateFieldName, false);

        return;
      }

      // if the selected item is "Manage products", then redirect to products page
      if (value.id === MANAGE_PRODUCT_ITEM.id) {
        navigate(PRODUCTS_PAGE.path);
        return;
      }

      // One time line item selection using autocomplete but not from existing products. Selected by 'create new' option
      if (value.type === LineItemType.OneTime) {
        setFieldValue(priceIdFieldName, '');
        setFieldValue(typeFieldName, LineItemType.OneTime);
        setFieldValue(`fields.lineItems[${itemIndex}].productId`, '');

        return;
      }

      setFieldValue(descriptionFieldName, value.name);

      if (EnableImmersiveProductCreationFlow && !isOneTime) {
        const selectedProduct = products.find((p) => p.id === value.productId);
        if (!selectedProduct) return;

        const validPrices = getValidPricesForLineItem(selectedProduct);
        const selectedPrice = validPrices.find((p) => p.ref === value.id);
        if (selectedPrice) {
          setFieldValue(
            `${formikLineItemFieldName}[${itemIndex}].productId`,
            value.productId,
          );
          const newAmount = (Number(selectedPrice.amount) / 100).toString();
          setFieldValue(amountFieldName, newAmount);
          setFieldValue(rateFieldName, newAmount);
          setFieldValue(priceIdFieldName, selectedPrice.ref);
          setFieldValue(typeFieldName, LineItemType.Product);
          setFieldValue(isEditingFieldName, true);

          setFieldValue(isValidFieldName, true);
          // We validate total amount based on whether amount field has been touched or not.
          // Since in case of product line item, we are setting the amount field programmatically,
          // we need to set the field as touched to show the error message.
          setFieldTouched(amountFieldName, true);
          setFieldTouched(rateFieldName, true);
        }

        if (isBelongsToStore) {
          // For store line item, we update the formik state right away
          // as oppposed to other line items where we wait for the user to click save
          if (onLineItemSelected) {
            const formattedLineItem = {
              ...lineItems[itemIndex],
              productId: value.productId || '',
              type: LineItemType.Product,
              priceId: selectedPrice?.ref || '',
              isValid: true, // Ensure the formatted line item is marked as valid
            };

            onLineItemSelected(formattedLineItem, itemIndex);
          }
        }
      } else {
        const selectedProduct = products.find((p) => p.id === value.id);
        if (!selectedProduct) return;

        const isAnInsertAfterFirstProduct =
          firstProductLineItemIndex !== itemIndex;

        const latestPrice = getValidPricesForLineItem(
          selectedProduct,
          isAnInsertAfterFirstProduct,
        ).at(0);

        if (latestPrice) {
          setFieldValue(
            `${formikLineItemFieldName}[${itemIndex}].productId`,
            value.id,
          );
          const newAmount = (Number(latestPrice.amount) / 100).toString();
          setFieldValue(amountFieldName, newAmount);
          setFieldValue(rateFieldName, newAmount);
          setFieldValue(priceIdFieldName, latestPrice.ref);
          setFieldValue(typeFieldName, LineItemType.Product);
          setFieldValue(isEditingFieldName, true);
          // We validate total amount based on whether amount field has been touched or not.
          // Since in case of product line item, we are setting the amount field programmatically,
          // we need to set the field as touched to show the error message.
          setFieldTouched(amountFieldName, true);
          setFieldTouched(rateFieldName, true);
        }

        if (isBelongsToStore) {
          // For store line item, we update the formik state right away
          // as oppposed to other line items where we wait for the user to click save
          if (onLineItemSelected) {
            const formatedLineItem = {
              ...lineItems[itemIndex],
              productId: value.id,
              type: LineItemType.Product,
              priceId: latestPrice?.ref || '',
            };

            onLineItemSelected(formatedLineItem, itemIndex);
          }
        }
      }
    },
    [
      showAllProducts,
      setFieldValue,
      descriptionFieldName,
      EnableImmersiveProductCreationFlow,
      isOneTime,
      amountFieldName,
      priceIdFieldName,
      itemIndex,
      typeFieldName,
      isBelongsToStore,
      isEditingFieldName,
      setFieldTouched,
      rateFieldName,
      quantityFieldName,
      navigate,
      products,
      getValidPricesForLineItem,
      formikLineItemFieldName,
      isValidFieldName,
      onLineItemSelected,
      lineItems,
      firstProductLineItemIndex,
    ],
  );

  const handleRemoveLineItem = useCallback(() => {
    onResetLineItem?.(itemIndex);
    setFieldTouched(descriptionFieldName, false);
    setFieldValue(shouldAddPriceFieldName, false);
    setdraftLineItem(null);
    if (lineItems.length > 1) {
      onRemoveLineItem(itemIndex);
    } else {
      setFieldValue(`${formikLineItemFieldName}.0`, {
        id: v4(),
        type: isBelongsToStore ? LineItemType.Product : LineItemType.OneTime,
        description: '',
        name: '',
        quantity: '1',
        amount: '',
        isValid: false,
        isEditing: false,
      });
    }
  }, [
    lineItems,
    itemIndex,
    amountFieldName,
    rateFieldName,
    setFieldTouched,
    formikLineItemFieldName,
  ]);

  const handleSaveItem = async () => {
    // Create price if it doesn't exist

    const isValidLineItem =
      !amountErrors && !errorQuantity && !errorDescription;
    if (
      lineItem.type === LineItemType.Product &&
      lineItem.shouldAddPrice &&
      isValidLineItem
    ) {
      const payload: CreatePriceInput = {
        productId: lineItem.productId,
        amount: lineItem.amount,
        type: interval ? PriceType.Recurring : PriceType.OneTime,
        currency: defaultCurrency.toLocaleLowerCase(),
      };
      if (interval) {
        payload.interval = interval;
      }

      const result = await addPrice(payload);
      if ('error' in result) return;
      if ('data' in result) {
        setFieldValue(isValidFieldName, true);
        setFieldValue(priceIdFieldName, result.data.ref);
        setFieldValue(shouldAddPriceFieldName, false);
      }
    }

    // Create product if it doesn't exist
    if (
      lineItem.type === LineItemType.OneTime &&
      lineItem.shouldAddProduct &&
      isValidLineItem
    ) {
      const basePrices = {
        amount: lineItem.amount,
        currency: defaultCurrency.toLocaleLowerCase(),
      };

      const itemPrices = interval
        ? {
            ...basePrices,
            type: PriceType.Recurring as const,
            interval: interval,
          }
        : {
            ...basePrices,
            type: PriceType.OneTime as const,
          };

      const payload: ProductSchema = {
        name: lineItem.description,
        prices: [itemPrices],
        imageUrls: [],
        description: '',
      };

      const product = await addProduct({
        product: payload,
        EnableImmersiveProductCreationFlow,
      });
      if ('error' in product) return;
      if ('data' in product) {
        setFieldValue(isValidFieldName, true);
        setFieldValue(typeFieldName, LineItemType.Product);
        setFieldValue(priceIdFieldName, product.data.price?.ref);
        setFieldValue(
          `${formikLineItemFieldName}[${itemIndex}].productId`,
          product.data.id,
        );
        setFieldValue(shouldAddPriceFieldName, false);
      }
    }

    if (onLineItemSelected && showAllProducts) {
      onLineItemSelected(lineItem, itemIndex);
    }

    // Verify that all fields are filled out and have no errors
    if (
      lineItem.amount &&
      (!isQuantityRequired || lineItem.quantity) &&
      lineItem[fieldName] &&
      isValidLineItem
    ) {
      setFieldValue(isEditingFieldName, false);
      setFieldValue(isValidFieldName, true);
    } else {
      setFieldTouched(amountFieldName, true);
      setFieldTouched(quantityFieldName, true);
      setFieldTouched(descriptionFieldName, true);
    }
    setdraftLineItem(null);
  };

  const handleDiscardItem = () => {
    // Verify if an existing line item is being edited
    // If so, revert the line item to its original state
    if (draftLineItem && 'isValid' in draftLineItem && draftLineItem.isValid) {
      setFieldValue(isEditingFieldName, false);
      setFieldValue(isValidFieldName, true);

      setFieldValue(descriptionFieldName, draftLineItem[fieldName]);
      setFieldValue(amountFieldName, draftLineItem.amount);
      setFieldValue(quantityFieldName, draftLineItem.quantity);
      setFieldValue(
        priceIdFieldName,
        'priceId' in draftLineItem && draftLineItem.priceId,
      );
      onResetLineItem?.(itemIndex);
    } else {
      // If a new line item is being added, clear the fields
      // and remove the line item
      setFieldValue(isValidFieldName, true);
      setFieldValue(isEditingFieldName, false);
      setFieldValue(shouldAddPriceFieldName, false);
      setFieldValue(descriptionFieldName, '');
      setFieldValue(amountFieldName, '');
      setFieldTouched(descriptionFieldName, false);
      handleRemoveLineItem();
    }

    setdraftLineItem(null);
  };

  // Triggers when user clicks on clear icon
  const handleClearItem = () => {
    setFieldValue(descriptionFieldName, '');
    onResetLineItem?.(itemIndex);
    setFieldValue(
      typeFieldName,
      isBelongsToStore ? LineItemType.Product : LineItemType.OneTime,
    );
    setFieldValue(amountFieldName, '');
    setFieldValue(isEditingFieldName, true);
    setFieldValue(shouldAddPriceFieldName, false);
    setFieldValue(shouldAddProductFieldName, false);
    setFieldValue(quantityFieldName, '1');
  };

  /**
   * Handle edit is mainly responsible for rendering the line item into form state
   * but in addition to that, it also takes care of side effects.
   * For example, there may be the case where an existing line item is being edited
   * In this case, we need to revert that line item to its original state and then open the subjected line item for editing.
   * Another case here is when a new line item is being added and user switches to edit another line item.
   * In this case, we need to remove the new line item and open the selected line item for editing.
   */
  const handleEditItem = () => {
    const currentEditingItemIndex = lineItems.findIndex(
      (x) => x.isEditing || !x.isValid,
    );
    // When an existing line item is being edited, revert the line item to its original state by setting the formik field values
    if (draftLineItem && 'isValid' in draftLineItem && draftLineItem.isValid) {
      if (currentEditingItemIndex > -1) {
        const editedLineItemFieldName = `${formikLineItemFieldName}[${currentEditingItemIndex}]`;
        setFieldValue(`${editedLineItemFieldName}.isEditing`, false);
        setFieldValue(`${editedLineItemFieldName}.isValid`, true);
        setFieldValue(
          `${editedLineItemFieldName}.${fieldName}`,
          draftLineItem[fieldName] || '',
        );

        setFieldValue(
          `${editedLineItemFieldName}.amount`,
          draftLineItem.amount,
        );
        setFieldValue(
          `${editedLineItemFieldName}.quantity`,
          draftLineItem.quantity,
        );
        setFieldValue(
          `${editedLineItemFieldName}.priceId`,
          'priceId' in draftLineItem && draftLineItem.priceId,
        );
      }
    } else if (!draftLineItem && currentEditingItemIndex > -1) {
      // When a new line item is being added and user switches to edit another line item, remove the new line item
      onRemoveLineItem(currentEditingItemIndex);
    }
    if (lineItem.type === LineItemType.Product && !lineItem.shouldAddPrice) {
      setFieldValue(priceIdFieldName, lineItem.priceId);
    }
    setdraftLineItem({ ...lineItem });
    setFieldValue(isEditingFieldName, true);
    setFieldValue(isValidFieldName, false);
  };

  return renderPresentationComponent({
    fieldName: `${formikLineItemFieldName}.${itemIndex}`,
    itemIndex,
    openOnFocus: showAllProducts,
    productDropdownOptions: productsOptions,
    onLineItemSelection: handleLineItemSelection,
    selectedProductItem,
    onFilterOptions: handleFilterOptions,
    lineItem,
    pricesDropdownOptions,
    lineItemsInCents,
    onPriceChange: handlePriceChange,
    onRemoveLineItem: handleRemoveLineItem,
    allowRecurringPrices,
    formikAmountFieldName,
    onSaveItem: handleSaveItem,
    onDiscardItem: handleDiscardItem,
    onClearItem: handleClearItem,
    onEditItem: handleEditItem,
    canBeRemoved: lineItems.length > 1,
    addProductRenderer,
    isSubmitting: isAddProductSavingInProgress || isAddPriceSavingInProgress,
    mode,
    allowCreatingNewProduct,
    allowCreatingNewPrice,
    lineItems,
    defaultCurrency,
    isDragging,
  });
}
